<script setup lang="ts">
import { useUserStore } from '@/stores/modules/user'
import type { AccRechargeRewardType, CrystalItemType } from '@/types/mall'
import { socketEmit, socketEmitAsync } from '@/utils/web-socket'
import { ref } from 'vue'
import { ElMessage } from 'element-plus'
import { formatNumberWithCommas } from '@/utils'
import { treasuryInfoList } from '@/views/treasury/data'
import { onBeforeMount } from 'vue'
import type { EquipItemType } from '@/types/equip'
import { getEquipName } from '@/utils/equip'
import EquipInfo from '@/views/item/components/EquipInfo.vue'
import { computed } from 'vue'
import { equipCollection } from '@/core/equip'
import { accRechargeReward } from '../data'
import { useCommonStore } from '@/stores/modules/common'

const commonStore = useCommonStore()
const userStore = useUserStore()

const commodityInfoList = [
  { name: '💎 钻石', key: 'diamond' },
  { name: '🔳 混沌宝匣', key: 'chaoticTreasure' },
  { name: '🔲 至臻宝匣', key: 'supremeTreasure' },
  ...treasuryInfoList,
]

const commodityList = ref<CrystalItemType[]>()
const curCommodity = ref<CrystalItemType>()
const buyCount = ref(1)
async function getCrystalStoreCommodity() {
  socketEmit<CrystalItemType[]>({
    type: 'getCrystalStoreCollection',
    callBack: (res) => {
      commodityList.value = res.data
    },
  })
}

const equipItemList = ref<EquipItemType[]>([])
const itemList = computed<EquipItemType[]>(() => {
  const items = equipItemList.value
  if (!items) return []
  return items.map((item) => {
    const { equipId } = item
    const { name, type, attrs } = equipCollection[equipId]
    return { ...item, name, type, attrs }
  })
})

const buyCountMap = ref<[number, number][]>([])
async function getUserBuyCount() {
  const res = await socketEmitAsync<[number, number][]>({ type: 'getThisWeekConsumablesBuyRecord' })
  buyCountMap.value = res
}

const resShow = ref(false)
function handleBuyCommodity() {
  if (!curCommodity.value) return ElMessage.warning('未选中任何商品')
  const { id, key, price } = curCommodity.value
  const name = commodityInfoList.find((info) => info.key === key)?.name
  const totalPrice = price * buyCount.value
  if (userStore.crystal < totalPrice) return ElMessage.warning('水晶不足')
  if (userStore.advanceLevel !== 4 && [1003, 1004].includes(id)) return ElMessage.warning('该商品需进阶后才可购买')
  if (userStore.soulType <= 3 && [1010, 1011].includes(id)) return ElMessage.warning('该商品需进阶后才可购买')
  ElMessageBox.confirm(`确认购买道具【${name}】吗？本次购买花费<b class="crystal">${price * buyCount.value}</b>水晶`, '提示', {
    type: 'warning',
    dangerouslyUseHTMLString: true,
    appendTo: 'body',
    confirmButtonClass: 'is-plain',
  })
    .then(async () => {
      const { itemList } = await socketEmitAsync<{ itemList: EquipItemType[] }>({
        type: 'buyCrystalCommodity',
        data: { id, count: buyCount.value },
      })
      userStore.crystal -= totalPrice
      const commodity = commodityList.value!.find((item) => item.id === id)
      if (!commodity) return

      switch (commodity.key) {
        case 'diamond': {
          userStore.diamond += totalPrice
          break
        }
        case 'chaoticTreasure': {
          userStore.itemList.push(...itemList)
          equipItemList.value = itemList
          resShow.value = true
          break
        }
        case 'supremeTreasure': {
          userStore.itemList.push(...itemList)
          equipItemList.value = itemList
          resShow.value = true
          break
        }
        default: {
          userStore.consumables[commodity.key] += buyCount.value
        }
      }
      getUserBuyCount()
    })
    .catch(() => {})
}

const accRechargeShow = ref(false)
const accRechargeCount = ref(0)
function handleOpenAccRecharge() {
  accRechargeShow.value = true
  socketEmitAsync<number>({
    type: 'getAccumulatedRecharge',
  }).then((res) => {
    accRechargeCount.value = res
  })
}
function getAccRechargeTrColor(item: AccRechargeRewardType) {
  if (item.needCount > accRechargeCount.value) {
    return 'var(--el-color-danger-light-5)'
  } else if (item.level <= userStore.rechargeLevel) {
    return 'var(--el-color-warning-light-5)'
  } else {
    return 'var(--el-color-success-light-5)'
  }
}
async function handleReceiveAccRechageReward() {
  const res = await socketEmitAsync<number>({ type: 'receiveAccumulatedRechargeReward' })
  userStore.rechargeLevel = res
  commonStore.getMailList()
  ElMessage.success('领取成功，请在邮件处查看奖励')
  accRechargeShow.value = false
}

onBeforeMount(() => {
  getCrystalStoreCommodity()
  getUserBuyCount()
})
</script>

<template>
  <div class="commodity">
    <p class="common-coin-info">🔮 {{ formatNumberWithCommas(userStore.crystal) }}</p>
    <div class="commodity-list-wrap border-wrap">
      <h4>商品列表</h4>
      <p style="font-size: 12px">* 周限购商品每周日0点刷新</p>
      <div class="commodity-list">
        <div class="item" v-for="item of commodityList" :key="item.id">
          <el-button
            size="small"
            type="primary"
            plain
            @click="
              () => {
                curCommodity = item
                buyCount = 1
              }
            "
            :style="{ borderColor: curCommodity?.id === item.id ? 'red' : '' }"
            >{{ commodityInfoList.find((info) => info.key === item.key)?.name }}{{ [1008, 1012].includes(item.id) ? ' (特惠款)' : '' }}</el-button
          >
        </div>
      </div>
    </div>
    <div class="commodity-detail border-wrap">
      <h4>商品详情</h4>
      <p>名称：{{ commodityInfoList.find((info) => info.key === curCommodity?.key)?.name || '未选择' }}</p>
      <p>
        说明：<span>{{ curCommodity?.intro }}</span>
      </p>
      <p>
        购买数量：
        <el-input-number
          size="small"
          :min="1"
          :max="(curCommodity?.weekMax || 1000000) - (buyCountMap?.find(([id]) => curCommodity?.id === id)?.[1] || 0)"
          v-model="buyCount"
          :precision="0"
          :disabled="!curCommodity"
        ></el-input-number>
      </p>
      <p>
        <span></span>价格：{{ (curCommodity?.price || 0) * buyCount }}🔮
        <span>
          <span style="margin-left: 15px" v-show="curCommodity?.weekMax"
            >周限购：{{ buyCountMap?.find(([id]) => curCommodity?.id === id)?.[1] || 0 }} / {{ curCommodity?.weekMax || '∞' }}</span
          ></span
        >
      </p>
      <el-button
        type="primary"
        plain
        style="width: 100%"
        @click="handleBuyCommodity"
        :disabled="userStore.advanceLevel !== 4 && [1003, 1004].includes(curCommodity?.id || 0)"
      >
        {{ userStore.advanceLevel !== 4 && [1003, 1004].includes(curCommodity?.id || 0) ? '未达购买条件' : '购入' }}
      </el-button>
    </div>
    <el-button type="success" plain @click="handleOpenAccRecharge" style="margin-top: 10px">赞助奖励</el-button>

    <el-dialog v-model="resShow" width="100%" style="max-width: 500px">
      <h2 class="affix">
        <span class="grow">恭喜获得</span>
      </h2>
      <div class="btn-wrap" style="max-height: 50vh; overflow: auto">
        <el-row :gutter="10">
          <el-col :span="12" v-for="item of itemList" :key="item.id">
            <el-popover placement="bottom" width="300px" trigger="click">
              <template #reference>
                <CommonBtn size="small">
                  <span v-html="getEquipName(item, item.type)"></span>
                </CommonBtn>
              </template>
              <EquipInfo :equip-info="item" :show-action="false" style="max-height: 55vh" />
            </el-popover>
          </el-col>
        </el-row>
      </div>
      <template #footer>
        <p style="text-align: center">
          <el-button plain @click="resShow = false" size="small">关闭</el-button>
        </p>
      </template>
    </el-dialog>

    <el-dialog v-model="accRechargeShow" width="100%" style="max-width: 500px">
      <h2 class="affix">
        <span class="grow">赞助奖励</span>
      </h2>
      <p>您的赞助水晶：🔮 {{ formatNumberWithCommas(accRechargeCount) }}</p>
      <table border style="text-align: center; width: 100%">
        <tr>
          <td>赞助档位</td>
          <td>奖励</td>
        </tr>
        <tr v-for="item of accRechargeReward" :style="{ backgroundColor: getAccRechargeTrColor(item) }">
          <td>🔮 {{ formatNumberWithCommas(item.needCount) }}</td>
          <td>
            <p v-if="item.reward.gold">💰 金币：{{ formatNumberWithCommas(item.reward.gold) }}</p>
            <p v-if="item.reward.diamond">💎 钻石：{{ formatNumberWithCommas(item.reward.diamond) }}</p>
            <p v-if="item.reward.crystal">🔮 水晶：{{ formatNumberWithCommas(item.reward.crystal) }}</p>
            <template v-if="item.reward.consumables">
              <p v-for="treasury of treasuryInfoList" v-show="(item.reward.consumables[treasury.key] || 0) !== 0">
                {{ treasury.name }}：{{ item.reward.consumables[treasury.key] }}
              </p>
            </template>
            <p v-if="item.level === 8">
              <span
                class="nickname"
                style="background: linear-gradient(135deg, #ffafaf 0%, #ffffaf 20%, #afffaf 40%, #afffff 60%, #afafff 80%, #ffafff 100%)"
                >特殊昵称颜色</span
              >
            </p>
          </td>
        </tr>
      </table>
      <template #footer>
        <p style="text-align: center">
          <el-button type="success" plain @click="handleReceiveAccRechageReward" size="small">领取</el-button>
          <el-button plain @click="accRechargeShow = false" size="small">关闭</el-button>
        </p>
      </template>
    </el-dialog>
  </div>
</template>

<style lang="scss" scoped>
.commodity {
  height: 100%;
  display: flex;
  flex-flow: column nowrap;
  .border-wrap {
    padding: 10px;
  }
  .commodity-list-wrap {
    margin-bottom: 10px;
    overflow: auto;
    flex: 1;
    .commodity-list {
      display: flex;
      flex-flow: row wrap;
      justify-content: space-between;
      .item {
        width: calc(50% - 5px);
        margin-bottom: 10px;
        button {
          width: 100%;
        }
      }
    }
  }
  .commodity-detail {
    p {
      margin: 6px 0;
      font-size: 14px;
    }
  }
  :deep() {
    .el-button:focus,
    .el-button:hover {
      background-color: unset;
    }
  }
  .btn-wrap {
    & > div {
      justify-content: unset;
    }
    .common-btn-wrap {
      margin-bottom: 10px;
    }
  }
}
</style>
